/**
 * Represents a parameter found in a prompt
 */
export interface PromptParameter {
  name: string;
  displayName: string;
  placeholder: string;
  originalText: string;
}

/**
 * Parse prompt text to extract parameters in {variable} format
 * @param promptText The prompt text to parse
 * @returns Array of parameters found
 */
export function parsePromptParameters(promptText: string): PromptParameter[] {
  const parameters: PromptParameter[] = [];
  const seen = new Set<string>();

  // Match {variableName} pattern
  const regex = /\{([^}]+)\}/g;
  let match;

  while ((match = regex.exec(promptText)) !== null) {
    const variableName = match[1].trim();

    // Skip if already seen (avoid duplicates)
    if (seen.has(variableName)) {
      continue;
    }

    seen.add(variableName);

    parameters.push({
      name: variableName,
      displayName: formatDisplayName(variableName),
      placeholder: `Enter ${formatDisplayName(variableName).toLowerCase()}`,
      originalText: match[0], // The full {variableName} text
    });
  }

  return parameters;
}

/**
 * Format variable name into a human-readable display name
 * Examples:
 * - "warehouse" -> "Warehouse"
 * - "legal_entity" -> "Legal Entity"
 * - "noOfDays" -> "No Of Days"
 * - "customerID" -> "Customer ID"
 */
function formatDisplayName(variableName: string): string {
  // Replace underscores with spaces
  let result = variableName.replace(/_/g, ' ');

  // Split camelCase and PascalCase
  result = result.replace(/([a-z])([A-Z])/g, '$1 $2');
  result = result.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');

  // Capitalize first letter of each word
  result = result
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return result;
}

/**
 * Substitute parameter values into prompt text
 * @param promptText Original prompt text with {variables}
 * @param parameters Array of parameters (from parsePromptParameters)
 * @param values Object mapping parameter names to their values
 * @returns Prompt text with all parameters replaced
 */
export function substituteParameters(
  promptText: string,
  parameters: PromptParameter[],
  values: Record<string, string>
): string {
  let result = promptText;

  for (const param of parameters) {
    const value = values[param.name] || '';

    // Escape special regex characters in the original text
    const escapedOriginal = param.originalText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedOriginal, 'g');

    result = result.replace(regex, value);
  }

  return result;
}

/**
 * Inject company parameter into prompt text
 * Replaces "company XXXX" with the configured company code
 * If no company reference exists, prepends "For company {code}, " to the message
 * @param promptText The prompt text
 * @param companyCode The company code to inject
 * @returns Prompt text with company parameter injected
 */
export function injectCompanyParameter(promptText: string, companyCode: string): string {
  // Check if message already contains "company" reference
  const companyRegex = /company\s+\w+/i;

  if (companyRegex.test(promptText)) {
    // Replace existing company reference
    return promptText.replace(companyRegex, `company ${companyCode}`);
  }

  // No company parameter - prepend it
  return `For company ${companyCode}, ${promptText}`;
}
